import { RefObject, useLayoutEffect, useRef, useState } from 'react';

/**
 * Tracks the live `offsetHeight` of an element via `ResizeObserver`.
 *
 * Useful for stacking sticky elements where one's `top` offset must equal the
 * rendered height of another (avoids brittle hardcoded pixel guesses).
 *
 * @param initialHeightPx Height used until the element is first measured.
 * @param remountDep Pass a value that changes whenever the measured element is
 *        REMOUNTED (e.g. it moves between conditional branches) — the observer
 *        is bound to the DOM node, so without re-attaching it would keep
 *        watching the unmounted node and the height would go stale.
 * @returns `{ ref, height }` — attach `ref` to the element; `height` is its
 *          current `offsetHeight` in pixels.
 */
export const useMeasuredHeight = <T extends HTMLElement = HTMLDivElement>(
  initialHeightPx = 0,
  remountDep?: unknown
): { ref: RefObject<T | null>; height: number } => {
  const ref = useRef<T>(null);
  const [height, setHeight] = useState(initialHeightPx);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const measure = () => setHeight(node.offsetHeight);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [remountDep]);

  return { ref, height };
};
